import { create, StateCreator } from 'zustand'
import { persist, PersistOptions } from 'zustand/middleware'

import { BookingWidgetData } from 'lib/components/BookingWidget/BookingWidget'
import { AddressInputAddress } from 'lib/components/Form/AddressInput/AddressInput'

import { RecognizedPlace } from '@/hooks/useAutoCompleteAddress'
import { ShipRoute } from '@/types/order'
import type { ShipmentDatesSegment } from '@/types/shipment'

const STORE_NAME = 'shipment-store'

// State

export type ShipmentState = {
  hasHydrated?: boolean
  shipment: Shipment
}

export type Shipment = {
  cruise?: boolean
  isFromBookingWidget?: boolean
  recognized_places: (RecognizedPlace | null)[]
  segments: AddressInputAddress[]
  shipRoute?: ShipRoute
  shipment_dates?: ShipmentDatesSegment[]
  type?: TripType
}

export type TripType = 'roundtrip' | 'oneway' | 'multi' | 'oneway-from-camp' | 'oneway-to-camp'

export const initState: ShipmentState = {
  shipment: {
    isFromBookingWidget: false,
    type: 'roundtrip',
    cruise: undefined,
    segments: [],
    shipment_dates: undefined,
    shipRoute: undefined,
    recognized_places: [],
  },
}

// Actions

export type ShipmentActions = {
  clearShipment: () => void
  initRoute: () => ShipRoute
  setHasHydrated: (hasHydrated: boolean) => void
  setRecognizedPlaces: (places: (RecognizedPlace | null)[]) => void
  setShipment: (shipment: Shipment) => void
  setShipmentFromBookingWidget: (
    data: BookingWidgetData,
    recognized_places: (RecognizedPlace | null)[],
    segments: AddressInputAddress[],
    shipRoute: ShipRoute,
  ) => void
}

// Store

export type ShipmentStore = ShipmentState & ShipmentActions

const shipmentStoreCreator: StateCreator<ShipmentStore> = (set) => ({
  ...initState,
  clearShipment: () => set(() => initState),
  initRoute: () => ({
    origin: {
      address1: '',
      address2: '',
      countryCode: 'US',
      companyName: '',
      state: '',
      city: '',
      postalCode: '',
    },
    destination: {
      address1: '',
      address2: '',
      countryCode: 'US',
      companyName: '',
      state: '',
      city: '',
      postalCode: '',
    },
  }),
  setHasHydrated: (hasHydrated) => set({ hasHydrated }),
  setShipment: (shipment) => set({ shipment }),
  setShipmentFromBookingWidget: (form, recognized_places, segments, shipRoute) =>
    set({
      shipment: {
        isFromBookingWidget: true,
        type: form?.tripType,
        segments,
        shipRoute,
        cruise: form?.isCruise,
        shipment_dates: [],
        recognized_places,
      },
    }),
  setRecognizedPlaces: (places) =>
    set((state) => ({
      shipment: {
        ...state.shipment,
        recognized_places: places,
      },
    })),
})

const useShipmentStore = create<ShipmentStore>()(
  persist(shipmentStoreCreator, {
    name: STORE_NAME,
    onRehydrateStorage: () => (state) => state?.setHasHydrated(true),
  } as PersistOptions<ShipmentStore>),
)

export default useShipmentStore
